// OneDrive:IgnoreCodeCoverage
import Async from '../../async/Async';
import PageTransitionType from './PageTransitionType';
import { IRUMOneData } from './IRUMOneData';
import { RUMOneDataUpload as RUMOneDataUploadEvent } from '../events/RUMOneDataUpload.event';
import PlatformDetection from '../../browser/PlatformDetection';
import { PerfMark, MARKER_PREFIX, getMarkerTime, mark, clearMarks, getAllMarks } from '../../performance/PerformanceMarker';

// only chrome has implementation of performance.memory
export interface IMemoryInfo {
    jsHeapSizeLimit: number;
    totalJSHeapSize: number;
    usedJSHeapSize: number;
}
declare const performance: Performance & {
    memory?: IMemoryInfo;
};

enum PerformanceDataState {
    /**
     * Not enought data collected to upload
     * At this state logger isRunning is true
     */
    Incomplete = 1,

    /**
     * Required data collected and can be uploaded.
     * Practically all expected controls has rendered by this time
     * and EUPL has been set.
     * At this state logger isRunning is true but sooner to change to false.
     */
    ReadyToUpload = 2,

    /**
     * Incomplete -> ReadyToUpload |  ERROR_TIMEOUT time elapsed -> Uploaded
     * Performance data has been uploaded for the current page.
     * Until reset, no more data will be uploaded.
     */
    Uploaded = 3,

    /**
     * Incomplete -> ERROR_TIMEOUT time elapsed -> TimeOut -> Uploaded
     * Timeout occured before expected controls could finish rendering
     * Data will be uploaded as it is but without EUPL.
     * Consumer should consider EUPL="" as > 30 sec for performance analysis
     */
    TimeOut = 4,

    /**
     * Incomplete -> (ERROR_TIMEOUT time elapsed && TimeOut) | ReadyToUpload -> Skipped
     * This is a state where data has been collected or timeout but uploading the data was skipped
     * This happens when consumer has asked to do so based on the page's debug environment
     */
    Skipped = 5
}

export interface IRUMOneLoggerConfig {
    checkInterval: number;
    error_timeout: number;
    max_markers: number;
}

/**
 * EUPL breakdown property value type, set needFlatten to be true when you want to send this property
 * as an individule column to Aria, default is false
 */
export interface IBreakdownValue {
    data: number | string | boolean;
    needFlatten?: boolean;
}

/**
 * property bag to store arbitary breakdown data,
 */
export interface IBreakdown {
    [key: string]: number | string | boolean | IBreakdownValue;
}

/**
 * internal interface for EUPLBreakdown, pure name value pair
 */
interface IBreakdownData {
    [key: string]: number | string | boolean;
}

export interface IControl {
    controlId: string;
    ignoreForEUPL?: boolean;
    data?: ControlPerformanceData;
}

export interface IError {
    reason: string;
    details: any;
}

export type ControlType = IControl | string;

export class APICallPerformanceData {
    public url: string;
    public duration: number;
    public correlationId: string;
    public status: number;
    public startTime: string;
    public endTime: string;
    public name: string;

    constructor(
        url: string,
        duration: number,
        correlationid: string,
        status: number,
        startTime: string,
        endTime: string,
        name?: string) {
        this.url = url;
        this.duration = duration;
        this.correlationId = correlationid;
        this.status = status;
        this.startTime = startTime;
        this.endTime = endTime;
        this.name = name;
    }
}

// Default key metrics which are always required before perf perf data is uploaded.
// For any additional key metric call addExpectedKeyMetric API
const DEFAULT_KEY_METRICS: ReadonlyArray<string> = ['EUPL', 'ScenarioId'];
// Each property column should confirm to this regex to align with Aria column name restriction.
const PROPERTY_NAME_REGEX: RegExp = /^[a-zA-Z0-9](([a-zA-Z0-9|_|\.]){0,98}[a-zA-Z0-9])?$/;

export class ControlPerformanceData {
    public controlId: string;
    public startTime: number;
    public endTime: number;
    public renderTimeCalculator: (rumone: RUMOneLogger, controlData: ControlPerformanceData) => number;
    public renderTimeRequiredDataChecker: (rumone: RUMOneLogger, controlData: ControlPerformanceData) => boolean;
    public renderTime: number;
    // if control is set to be secondary and ignore for EUPL, EUPL calculation is not based on its render time,
    // but we will report its render time as part of EUPL breakdown
    public ignoreForEUPL: boolean;
    public needFlatten?: boolean;

    constructor(
        controlId: string,
        startTime: number,
        endTime: number,
        renderTimeCalculator: (rumone: RUMOneLogger, controlData: ControlPerformanceData) => number,
        renderTimeRequiredDataChecker: (rumone: RUMOneLogger, controlData: ControlPerformanceData) => boolean,
        ignoreForEUPL?: boolean,
        needFlatten?: boolean) {
        this.controlId = controlId;
        this.startTime = startTime;
        this.endTime = endTime;
        this.renderTimeCalculator = renderTimeCalculator;
        this.renderTimeRequiredDataChecker = renderTimeRequiredDataChecker;
        this.ignoreForEUPL = ignoreForEUPL === undefined ? true : ignoreForEUPL;
        this.needFlatten = needFlatten === undefined ? false : needFlatten;
    }
}

function isNullOrUndefined(item: any): boolean {
    'use strict';
    return item === null || typeof item === 'undefined';
}

const DEFAULT_LOGGER_NAME = '__default_rumone__';
const DEFAULT_CONFIG: IRUMOneLoggerConfig = {
    checkInterval: 100,   // in milliseconds
    error_timeout: 30000,   // in milliseconds
    max_markers: 100   // suppport maximum 100 perf markers
};
/**
 * It is a new client side perf instrumentation, it logs more metrics, like scenario, api data, server request id, duration, etc in 1 single schema.
 * It has server side usage DB and cosmos supports.
 */
export default class RUMOneLogger {
    public static rumOneLogger: RUMOneLogger = undefined;
    public static Loggers: { [name: string]: RUMOneLogger } = {};
    private static _markers: { [name: string]: string[] } = {};
    private _name: string;
    private _config: IRUMOneLoggerConfig;
    private _keyMetrics: string[] = DEFAULT_KEY_METRICS as string[];
    private async = new Async(this);
    private dataStartTime: number = Number((new Date()).getTime());
    private _rumoneData: IRUMOneData = {};
    private dataState: PerformanceDataState = PerformanceDataState.Incomplete;
    private controls: Array<IControl> = [];
    private apis: Array<APICallPerformanceData> = [];
    private perfDataTimer: number;
    private loggingFunc: (streamName: string, dictProperties: any) => void;
    private euplBreakDown: IBreakdownData = {};
    private serverMetrics: IBreakdownData = {};
    private isW3cTimingCollected: boolean = false;
    private isW3cResourceTimingCollected: boolean = false;
    private tempData: any = {};
    private _platformDetection: PlatformDetection;
    private _waitOnAddingExpectedControl: boolean;
    private _excludePageData: boolean;
    private _emergencyUpdateFunc: () => void = this._emergencyUpload.bind(this);

    /**
     * Optional handler notified when EUPL is computed for the current page.
     */
    private _EUPLComputedHandler: (eupl: number) => void;

    constructor(
        name: string,
        logFunc: (streamName: string, dictProperties: any) => void,
        rumOneconfig: IRUMOneLoggerConfig = DEFAULT_CONFIG
    ) {
        this._name = name;
        this.loggingFunc = logFunc;
        this._config = rumOneconfig;
        this.setPerfDataTimer();
        this._platformDetection = new PlatformDetection();
        this._hookUnloadEvent();
    }

    /**
     * * RUMOneLogger.getRUMOneLogger: Use this method to get an intance of RUMOneLogger
     * with default parameters.
     * @param name: name of the instance of RUMOneLogger.
     * If not provided, the default singleton RUMOneLogger instance will be returned
     * @param logFunc: method of uploading collected RUMOne data to telemetry data storage
     * @param rumOneConfig: configuration of RUMOneLogger, do not pass in and use default if you
     * are not sure
     */
    public static getRUMOneLogger(
        name: string = DEFAULT_LOGGER_NAME,
        logFunc?: (streamName: string, dictProperties: any) => void,
        rumOneConfig: IRUMOneLoggerConfig = DEFAULT_CONFIG): RUMOneLogger {
        const loggingFunc = logFunc || ((streamName: string, dictProperties: any) => {
            RUMOneDataUploadEvent.logData({ streamName: streamName, dictionary: dictProperties });
        });
        if (!RUMOneLogger.Loggers[name]) {
            try {
                const logger = new RUMOneLogger(name, loggingFunc, rumOneConfig);
                if (name === DEFAULT_LOGGER_NAME) {
                    RUMOneLogger.rumOneLogger = logger;
                }
                RUMOneLogger.Loggers[name] = logger;
            } catch (e) {
                // If RUMOneLogger fails, don't block UX
            }
        }
        return RUMOneLogger.Loggers[name];
    }

    // For backward compatibility, will be removed once addExpectedKeyMetric API is adopted
    public get KeyMetrics(): string[] {
        return this._keyMetrics;
    }

    public getPerformanceData(): IRUMOneData {
        return this._rumoneData;
    }
    public resetLogger() {
        this._emergencyUpload(); // upload ready key metrics if we have not. this should rarely happen
        this.dataStartTime = (new Date()).getTime();
        this.dataState = PerformanceDataState.Incomplete;
        this.isW3cTimingCollected = false;
        this.isW3cResourceTimingCollected = false;
        this.controls = [];
        this.apis = [];
        this.tempData = {};
        this._rumoneData = {};
        this._excludePageData = false;
        this._waitOnAddingExpectedControl = false;
        this._keyMetrics = DEFAULT_KEY_METRICS as string[];
        this.clearPerfDataTimer();
        this.setPerfDataTimer();
        this.euplBreakDown = {};
        this.serverMetrics = {};
        this.clearResourceTimings();
        clearMarks(this._getOwnMakers().map((marker: PerfMark) => marker.name));
        RUMOneLogger._markers[this._name] = [];
        this._unhookUnloadEvent();
        this._hookUnloadEvent();
        this.logMessageInConsole("Reset performance Logger Done");
    }

    /**
     * Consumer should own logic to determine whether to ignore the  page for perf data collection
     * when Called this API, RUMOneLogger will not upload data but display in the console for debug purpose
     * E.g. Hidden Preload.aspx page for sharepoint or a debug page with #debugManifest or ?moduleLoader= in the url
     *
     */
    public excludePageForPerfData(): void {
        this._excludePageData = true;
    }
    public getPerformanceDataPropertyValue(key: string): any {
        return key && this._rumoneData[key];
    }

    /**
     * Use this API carefully to add extra dynamic columns to Performance data.
     * addEUPLBreakdown with needFlatter can do same thing, but add duplicate data into two columns
     * CAUTION: Do not use this API for random name as it can explode number of columns
     * Use column names which are going to be consistent and don't explode in count over time.
     * Like name based on guid, timestamp or counters are bad examples to use as key.
     *
     * @param key Name of the dynamic coulmn confirming to Regex /^[a-zA-Z0-9](([a-zA-Z0-9|_|\.]){0,98}[a-zA-Z0-9])?$/
     * @param value Corresponding value
     *
     * Throws Error when key does not confirm to the Supported Regex
     */
    public setPerformanceDataPropertyValue(key: string, value: any): boolean {
        if (key && PROPERTY_NAME_REGEX.test(key)) {
            this._rumoneData[key] = value;
            return true;
        } else {
            console.error(`key ${key} does not confrim to the Regex ${PROPERTY_NAME_REGEX}`);
            return false;
        }
    }

    public addExpectedControl(control: ControlType) {
        const normalizedControl = this._normalizeControl(control);
        if (!this.expectingControl(control)) {
            this.controls.push(normalizedControl);
        } else {
            // Ensure expected controls are not ignored from EUPL
            this.controls.filter((expected: IControl) => expected.controlId === normalizedControl.controlId)[0].ignoreForEUPL = normalizedControl.ignoreForEUPL;
        }
    }
    public expectingControl(control: ControlType): boolean {
        const normalizedControl = this._normalizeControl(control);
        return this.controls.filter((expected: IControl) => expected.controlId === normalizedControl.controlId).length > 0;
    }
    public writeServerCorrelationId(correlationId: string) {
        this._rumoneData.ServerCorrelationId = this._rumoneData.ServerCorrelationId || correlationId;
    }
    public writeServerUrl(url: string) {
        if (!this._rumoneData.ServerUrl) {
            this._rumoneData.ServerUrl = url || window.location.href;
        }
    }
    public writeServerSideLatency(duration: number, iisLatency: number) {
        if (!this.isCollected('ServerRequestDuration')) {
            this._rumoneData.ServerRequestDuration = duration;
            this._rumoneData.IISLatency = iisLatency;
        }
    }
    public writeControlPerformanceData(controlData: ControlPerformanceData) {
        if (controlData) {
            const foundControl = this.controls.filter((control: IControl) => control.controlId === controlData.controlId)[0];
            if (foundControl) {
                if (!foundControl.data) {
                    foundControl.data = controlData;
                }
            } else {
                const control: IControl = {
                    controlId: controlData.controlId,
                    data: controlData,
                    ignoreForEUPL: controlData.ignoreForEUPL
                };
                this.controls.push(control);
            }
        }
    }
    public writeAPICallPerformanceData(apiData: APICallPerformanceData) {
        if (apiData) {
            this.apis.push(apiData);
        }
    }
    public readAPICallPerformanceData(): Array<APICallPerformanceData> {
        return this.apis;
    }
    public writeAppCache(appCache: boolean) {
        if (!this.isCollected('AppCache')) {
            this._rumoneData.AppCache = appCache;
        }
    }
    public writePageTransitionType(pageTransitionType: PageTransitionType, overwrite?: boolean) {
        if ((!this.isCollected('PageTransitionType') || overwrite) &&
            (pageTransitionType === PageTransitionType.fullPageLoad || pageTransitionType === PageTransitionType.none || pageTransitionType === PageTransitionType.onePageAppNavigation)) {
            this._rumoneData.PageTransitionType = pageTransitionType;
        }
    }
    public writeScenarioId(scenarioId: string, overwrite?: boolean) {
        if (!this.isCollected('ScenarioId') || overwrite) {
            this._rumoneData.ScenarioId = scenarioId;
        }
    }
    public collectW3CPerfTimings() {
        if (!this.isW3cTimingCollected) {
            var w3cTimeStampNames = ['navigationStart', 'unloadEventStart', 'unloadEventEnd', 'fetchStart', 'redirectStart', 'redirectEnd', 'domainLookupStart', 'domainLookupEnd', 'connectStart', 'secureConnectionStart', 'connectEnd', 'requestStart', 'responseStart', 'responseEnd', 'domLoading', 'domComplete', 'loadEventStart', 'loadEventEnd'];
            var perfTimingObject: PerformanceTiming = performance && performance.timing;

            if (perfTimingObject) {
                for (var index = 0; index < w3cTimeStampNames.length; index++) {
                    var w3cObject = perfTimingObject[w3cTimeStampNames[index]];
                    if (w3cObject) {
                        this._logRUMOneData(this.getW3cTimingName(w3cTimeStampNames[index]), Number(w3cObject));
                    }
                }
                this.isW3cTimingCollected = true;
            }
        }
    }
    public collectW3cResourceTimings() {
        if (window.document && !this.isW3cResourceTimingCollected && performance && performance.getEntriesByType) {
            var allRequests: Array<PerformanceResourceTiming> = performance.getEntriesByType("resource");

            var iFrames = document.getElementsByTagName("iframe");

            for (let index = 0; index < iFrames.length; index++) {
                var iFramePerformance = null;

                try {
                    iFramePerformance = iFrames[index].contentWindow.performance;
                } catch (e) {
                    // sometimes we can't access iFrame.
                }

                if (iFramePerformance && iFramePerformance.getEntriesByType) {
                    allRequests.concat(iFramePerformance.getEntriesByType("resource"));
                }
            }
            var fromSources = {};
            fromSources["SharePoint"] = (element: PerformanceResourceTiming) => { return /\.sharepoint\.com|\.spoppe\.com/i.test(element.name); };  //matching sharepoint.com or spoppe.com
            fromSources["CDN"] = (element: PerformanceResourceTiming) => { return /(cdn(ppe)?|static(ppe)?)\.sharepointonline\.com|\.akamaihd\.net/i.test(element.name); };  //matching cdn(ppe)sharepointonline.com or adamaihd.net
            fromSources["ThirdParty"] = (element: PerformanceResourceTiming) => { return !fromSources["SharePoint"](element) && !fromSources["CDN"](element); };  // all requests other than sharepoint requests or cdn requests

            var types = {};
            types["ASPX"] = (element: PerformanceResourceTiming) => { return /\.aspx/i.test(element.name); };
            types["JS"] = (element: PerformanceResourceTiming) => { return /script/i.test(element.initiatorType); };
            types["CSS"] = (element: PerformanceResourceTiming) => { return /link|css/i.test(element.initiatorType); };
            types["IMG"] = (element: PerformanceResourceTiming) => { return /img/i.test(element.initiatorType); };

            var sources: Array<string> = Object.keys(fromSources);
            for (let index = 0; index < sources.length; index++) {
                var source = sources[index];
                var requests = this.categorizeResourceRequests(allRequests, {
                    from: fromSources[source],
                    requestType: null        //match any type to get total request count for this source
                });
                this._logRUMOneData(source + "RequestCountTotal", requests.length); //SharePoint|CDN|ThirdParty resource request count

                var typeKeys: Array<string> = Object.keys(types);
                for (var typeIndex = 0; typeIndex < typeKeys.length; typeIndex++) {
                    var type = typeKeys[typeIndex];
                    this._logRUMOneData(source + "RequestCount" + type, this.categorizeResourceRequests(requests, {
                        from: null,     // requests already only contains the requests from SharePoint|CDN|ThirdParty source
                        requestType: types[type]
                    }).length);   //SharePoint|CDN|ThirdParty ASPX resource request count
                }

                if (requests.length > 0) {
                    this._logRUMOneData(source + "RequestDownloadTime", Math.round(requests.reduce((sum: number, currentVal: PerformanceResourceTiming) => {
                        return sum + currentVal.duration;
                    }, 0) / requests.length));  //SharePoint|CDN|ThirdParty resource request average duration
                    // log the file names of all resource requests in a JSON string. The output after processing will looks like:
                    // [{name: "require-db6c47e2.js", startTime: 500, duration: 100},{name: "RenderListDataAsStream", startTime: 200, duration: 10}]
                    // The raw resource name before this processing is "https://msft.spoppe.com/teams/SPGroups/_api/web/GetList(@listUrl)/RenderListDataAsStream?Paged=TRUE&p_FileLeafRef=test%2eurl&p_ID=213&PageFirstRow=121&View=6eab4254-2f2f-4086-91c0-549ae900cc93&@listUrl=%27%2Fteams%2FSPGroups%2FVNextDocLib%27"
                    const files = JSON.stringify(requests.map((timing: PerformanceResourceTiming) => {
                        return {
                            name: timing.name.split("/").map((urlToken: string) => {
                                return urlToken.split("?")[0];
                            }).filter((urlToken: string) => {
                                return urlToken && urlToken.length > 0;
                            }).slice(-1)[0].replace(/\(.*?\)/g, '()'),
                            startTime: Math.round(timing.startTime),
                            duration: Math.round(timing.duration),
                            transferSize: timing['transferSize'] || 0
                        };
                    }));
                    this._logRUMOneData(source + "RequestNames", files);
                }
            }

            this.isW3cResourceTimingCollected = true;
        }
    }
    public saveTempData(key: string, value: any) {
        this.tempData[key] = value;
    }
    public readTempData(key: string): any {
        return this.tempData[key];
    }
    public writeRenderTime(renderTime: number, overwrite?: boolean) {
        if (!this.isCollected('RenderTime') || overwrite) {
            this._rumoneData.RenderTime = renderTime;
        }
    }
    public writePreRenderTime(preRender: number) {
        if (!this.isCollected('PreRender')) {
            this._rumoneData.PreRender = preRender;
        }
    }
    public writePostRenderTime(postRender: number) {
        if (!this.isCollected('PostRender')) {
            this._rumoneData.PostRender = postRender;
        }
    }
    public writeDataFetchTime(dataFetch: number, overwrite?: boolean) {
        if (!this.isCollected('DataFetch') || overwrite) {
            this._rumoneData.DataFetch = dataFetch;
        }
    }

    public isRunning(): boolean {
        return !(
            this.dataState === PerformanceDataState.Uploaded ||
            this.dataState === PerformanceDataState.TimeOut ||
            this.dataState === PerformanceDataState.Skipped);
    }

    public writeEUPLBreakdown(euplBreakdown: string, overwrite?: boolean) {
        if (euplBreakdown) {
            try {
                const breakdown = JSON.parse(euplBreakdown);
                for (const key in breakdown) {
                    if (!breakdown.hasOwnProperty(key)) {
                        continue;
                    }
                    this.addEUPLBreakdown(key, breakdown[key], overwrite);
                }
            } catch (e) {
                // in case the euplBreakdown is invalid JSON
                this.logMessageInConsole("Failed to write EUPL breakdown data:" + e.toString());
            }
        }
    }

    public addEUPLBreakdown(name: string, value: number | IBreakdownValue, overwrite?: boolean) {
        if (name && !isNullOrUndefined(value)) {
            if (isNullOrUndefined(this.euplBreakDown[name]) || overwrite) {
                const data: number | string | boolean = this._isBreakdownValue(value) ? value.data : value;
                this.euplBreakDown[name] = data;
            }
            //  kusto job to parse EUPLBreakdown property bag is expensive and cause a lot kusto/MDM perf issues.
            //  to workaround it, we flatten EUPLBreakdown properties that are marked with needFlatten===true to send
            //  them to aria as individual columns. After this change, kusto query does not need to parse json any more.
            if (this._isBreakdownValue(value) && value.needFlatten) {
                this._logRUMOneData(name, value.data);
            }
        }
    }

    public readEUPLBreakdown(name?: string): number | string | boolean | IBreakdown {
        return !!name ? this.euplBreakDown[name] : this.euplBreakDown;
    }

    /**
     * Add list of flights enabled for this page.
     */
    public addFlights(flights: string[]) {
        if (flights && flights.length > 0) {
            const _flights: string[] = JSON.parse(this.getPerformanceDataPropertyValue('Flights') || '[]');
            for (let index = 0; index < flights.length; index++) {
                const flight: string = flights[index];
                if (flight && _flights.indexOf(flight) === -1) {
                    _flights.push(flight);
                }
            }

            this._rumoneData.Flights = JSON.stringify(_flights);
        }
    }

    public addServerMetrics(metric: { [key: string]: number }, overwrite?: boolean) {
        if (metric) {
            for (const key in metric) {
                if (key && !isNullOrUndefined(metric[key]) &&
                    (isNullOrUndefined(this.serverMetrics[key]) || overwrite)) {
                    this.serverMetrics[key] = metric[key];
                }
            }
        }
    }

    /**
     * Write version of the Sharepoint server served the request
     */
    public writeServerVersion(version: string) {
        this._rumoneData.SharePointServerVersion = version;
    }

    public readControlPerformanceData(): Array<ControlPerformanceData> {
        return this.controls.filter((control: IControl) => !!control.data).map((control: IControl) => control.data);
    }

    public mark(name: string): void {
        const marks = RUMOneLogger._markers[this._name] = RUMOneLogger._markers[this._name] || [];
        marks.push(name);
        mark(name);
    }

    public now(): number {
        return performance && performance.now ? Math.round(performance.now()) : NaN;
    }

    /**
     * Register handler function that will be called back when EUPL is computed successfully.
     * @param handler function that app register, wiill be called back when EUPL is computed.
     */
    public registerEUPLComputedHandler(handler: (eupl: number) => void) {
        this._EUPLComputedHandler = handler;
    }

    /**
     * Register handler function that will be called back when timeout happens.
     * @param handler function that app register, will be called back when timeout happens
     */
    public registerErrorHandler(handler: (error: IError) => void) {
        /* no-op */
    }

    public getMarkerTime(name: string): number {
        return getMarkerTime(name);
    }

    /**
     * Add key expected metric. RUMONeLogger will wait upon these key metrics to be collected
     * before uploading performance data.
     * @param keyMetric
     */
    public addExpectedKeyMetric(keyMetric: string) {
        if (this._keyMetrics && this._keyMetrics.indexOf(keyMetric) <= -1) {
            this._keyMetrics.push(keyMetric);
        }
    }

    /**
     * If set to true, RUMOnelogger will wait until this is not set false again before concluding all expected controls are added
     * Set it to true if not all expected controls are added
     * Set it to false once all expected controls has been added
     */
    public set waitOnAddingExpectedControl(wait: boolean) {
        this._waitOnAddingExpectedControl = wait;
    }
    private _logRUMOneData(key: string, value: any) {
        if (key) {
            this._rumoneData[key] = value;
        }
    }
    private clearResourceTimings(): void {
        if (performance && performance.clearResourceTimings) {
            performance.clearResourceTimings();
        }
    }
    private _isBreakdownValue(data: number | IBreakdownValue): data is IBreakdownValue {
        return (<IBreakdownValue>data).needFlatten !== undefined;
    }
    private collectMarks(): void {
        const marks = {};
        let markerIndex = 0;
        this._getOwnMakers().forEach((mark: PerfMark) => {
            if (markerIndex < this._config.max_markers) {
                const markName = mark.name.substr(MARKER_PREFIX.length) + `.mark${markerIndex++}`;
                marks[markName] = Math.round(mark.startTime);  // covert to rumone collected marks to object and merge to EUPL Breakdown
            }
        });
        this.writeEUPLBreakdown(JSON.stringify(marks));
    }

    private _getOwnMakers(): PerfMark[] {
        const markers = RUMOneLogger._markers;
        const markersFromOtherInstances = [].concat(...Object.keys(markers).filter((name: string) =>
            markers.hasOwnProperty(name) && name !== this._name).map((name: string) => markers[name]));
        // include markers generated by current rumone instance and all externally created perf markers
        const results = getAllMarks().filter((mark: PerfMark) => markersFromOtherInstances.indexOf(mark.name) === -1);
        return results;
    }

    private logMessageInConsole(message: string) {
        if (this.isRUMOneDebuggingEnabled) {
            console.log(message);
        }
    }

    private get isRUMOneDebuggingEnabled(): boolean {
        try {
            if ('sessionStorage' in window && window.sessionStorage) {
                const debugStr = window.sessionStorage["enableRUMOneDebugging"];
                const debug = debugStr && debugStr.toLowerCase() === "true";
                return debug && typeof console !== "undefined" && !!console;
            }
        } catch (e) {
            // sessionStorage errors
        }

        return false;
    }

    private logObjectForDebugging(propertyName: string, dictProperties: any) {
        if (this.isRUMOneDebuggingEnabled) {
            const logMessageText: string = `${this._name} ${propertyName}` + " : " + JSON.stringify(dictProperties);
            console.log(logMessageText);
        }
    }

    private isCollected(name: string): boolean {
        return !isNullOrUndefined(this.getPerformanceDataPropertyValue(name));
    }

    private _normalizeControl(control: ControlType): IControl {
        return typeof control === 'string' ? {
            controlId: control,
            ignoreForEUPL: false
        } : control;
    }

    private setPerfDataTimer() {
        this.perfDataTimer = this.async.setTimeout(this.loopForDataCompleteness, this._config.checkInterval);
    }

    private clearPerfDataTimer() {
        if (!isNullOrUndefined(this.perfDataTimer)) {
            this.async.clearTimeout(this.perfDataTimer);
            this.perfDataTimer = null;
        }
    }

    private _hookUnloadEvent() {
        /* tslint:disable:ban-native-functions */
        if (window.addEventListener) {
            window.addEventListener('beforeunload', this._emergencyUpdateFunc);
        }
        /* tslint:enable:ban-native-functions */
    }

    private _unhookUnloadEvent() {
        /* tslint:disable:ban-native-functions */
        if (window.removeEventListener) {
            window.removeEventListener('beforeunload', this._emergencyUpdateFunc);
        }
        /* tslint:enable:ban-native-functions */
    }

    private categorizeResourceRequests(requests: Array<PerformanceResourceTiming>, categorizer: any): Array<PerformanceResourceTiming> {
        var ret = [];
        for (var index = 0; index < requests.length; index++) {
            var request = requests[index];
            if ((!categorizer.from || categorizer.from(request)) && (!categorizer.requestType || categorizer.requestType(request))) {
                ret.push(request);
            }
        }
        return ret;
    }

    private collectSupplementaryData(): void {
        this.setAPIDataToRUMOne();
        this.collectMarks();
        this.writeServerUrl(null);
        this.setBrowseInfo();
        this.setReferrer();
        this._collectMemoryData();
        this._rumoneData.ServerMetrics = JSON.stringify(this.serverMetrics);
        this._rumoneData.EUPLBreakdown = JSON.stringify(this.euplBreakDown);
    }

    private loopForDataCompleteness() {
        this.clearPerfDataTimer();

        if (!this._waitOnAddingExpectedControl) {
            if (this.isRUMOneDebuggingEnabled) {
                this.logMessageInConsole("====================================================================");
                this.logObjectForDebugging("RUMONE: ", this._rumoneData);
                this.logObjectForDebugging("RUMOne DataState: ", String(this.getReadableDataState(this.dataState)));
                this.logObjectForDebugging("Controls: ", this.controls);
                this.logObjectForDebugging("API Performance Data: ", this.apis);
                this.logObjectForDebugging("Temp Data: ", this.tempData);
                this.logObjectForDebugging("EUPLBreakdown: ", this.euplBreakDown);
                this.logObjectForDebugging("ServerMetrics: ", this.serverMetrics);
                this.logMessageInConsole("====================================================================\n\n");
            }

            if (!this.isRunning()) {
                return;
            }

            this._updateState();

            if (this.dataState === PerformanceDataState.ReadyToUpload) {
                this.finishPerfDataUpload();
                return;
            }

            this.processControlRenderTime();
            if (this.readyToComputeEUPL()) { // if all expected control data is available, compute EUPL
                this.setEUPL();
                this._updateState();
            }
        }

        // Check timeout
        this._checkTimeout();
        if (this.isRunning()) {
            this.setPerfDataTimer();
        }
    }

    /**
     * Check if we reached ERROR_TIMEOUT without being ready to upload and timeout if so
     */
    private _checkTimeout(): void {
        if (!this.isRunning() || this.dataState === PerformanceDataState.ReadyToUpload) {
            return;
        }

        if (Number((new Date()).getTime()) - Number(this.dataStartTime) <= this._config.error_timeout) {
            return; // Nope, we still have time
        }

        // Upload whatever data we have without all key metrics
        this.dataState = PerformanceDataState.TimeOut;
        const error: IError = {
            reason: 'TimeOut',
            details: {
                'MissingMetrics': this._getMissedKeyMetrics(),
                'MissingControls': this._getMissedControls()
            }
        };

        this._rumoneData.RUMOneError = JSON.stringify(error);
        this.finishPerfDataUpload();
    }

    private _updateState(): void {
        this.dataState =
            (this._getMissedKeyMetrics().length === 0 && this._getMissedControls().length === 0)
                ? PerformanceDataState.ReadyToUpload
                : PerformanceDataState.Incomplete;
    }

    /**
     * Get array of missing key metrices
     */
    private _getMissedKeyMetrics(): string[] {
        const missedKeyMetrics: string[] = [];
        for (var i = 0; i < this._keyMetrics.length; i++) {  // check if key metrics are collected
            var keyMetricValue: any = this.getPerformanceDataPropertyValue(this._keyMetrics[i]);
            if (isNullOrUndefined(keyMetricValue)) {
                missedKeyMetrics.push(this._keyMetrics[i]);
            }
        }

        return missedKeyMetrics;
    }

    /**
     * Get array of controls missing data
     */
    private _getMissedControls(): IControl[] {
        return this.controls.filter((control: IControl) => !control.data || !control.data.renderTime);
    }

    // since we allow secondary controls reported but not be used to calculate EUPL,
    // these controls might be very slow and we are in higher risky that end user navigates
    // away from this page before we upload our data although EUPL might be available already.
    // This should be hooked with onunload event and resetlogger method
    private _emergencyUpload(): void {
        if (this.isRunning() && this._getMissedKeyMetrics().length === 0) {
            this.finishPerfDataUpload(true);
        }
    }

    // if emergency set to true, we will not validate data state in uploadPerfData call
    // since current page is navigating away, mostly likely we already collected key metrics
    // only waiting for secondary controls, we should upload data collected so far
    private finishPerfDataUpload(emergency?: boolean): void {
        this.writeControlDataToRUMOne();
        this.collectSupplementaryData();
        try {
            this.uploadPerfData(emergency);
            if (this.isRUMOneDebuggingEnabled) {
                this.logMessageInConsole('Final Data uploaded');
                this.logObjectForDebugging("RUMONE: ", this._rumoneData);
                this.logObjectForDebugging("RUMOne DataState: ", this.getReadableDataState(this.dataState));
            }
        } catch (e) {
            ((errorText: string) => {
                if (typeof console !== "undefined" && Boolean(console)) {
                    console.error(errorText);
                }
            })(`PerformanceLogger error writing RUMOne data: ${e}`);
        } finally {
            this._unhookUnloadEvent();
        }
    }

    private getReadableDataState(_dataState: number): string {
        for (var key in PerformanceDataState) {
            if (_dataState === Number(PerformanceDataState[key])) {
                return key;
            }
        }
        return 'NaN';
    }

    /**
     * Write Control and corresponding render time for top 10 renderTime controls.
     */
    private writeControlDataToRUMOne() {
        if (this.controls && this.controls.length) {
            const byRenderTime: IControl[] = this.controls.slice(0).filter((control: IControl) => !!control.data && !!control.data.renderTime && !control.ignoreForEUPL);
            byRenderTime.sort(
                (control1: IControl, control2: IControl): number => {
                    return control2.data.renderTime - control1.data.renderTime;
                });
            // We have maximum 10 slots for the Control render data collection
            for (let index = 0; index < 10 && index < byRenderTime.length; index++) {
                this._logRUMOneData(`Control${index + 1}Id`, byRenderTime[index].controlId);
                this._logRUMOneData(`Control${index + 1}RenderTime`, byRenderTime[index].data.renderTime);
            }
            // for secondary controls, we wrote data into EUPL breakdown
            const secondaryControls: IBreakdown = {};
            this.controls.slice(0).filter((control: IControl) => !!control.data && !!control.data.renderTime && !!control.ignoreForEUPL).forEach((control: IControl) => {
                secondaryControls[control.controlId] = {
                    data: control.data.renderTime,
                    needFlatten: control.data.needFlatten
                };
            });
            if (Object.keys(secondaryControls).length > 0) {
                this.writeEUPLBreakdown(JSON.stringify(secondaryControls), true);
            }
        }
    }

    /**
     * Calculate renderTime for the controls ready for it.
     */
    private processControlRenderTime() {
        for (const control of this.controls) {
            // if this control is not processed yet and ready to be processed
            if (control.data && !control.data.renderTime && control.data.renderTimeRequiredDataChecker(this, control.data)) {
                control.data.renderTime = control.data.renderTimeCalculator(this, control.data);
            }
        }
    }
    private readyToComputeEUPL(): boolean {
        const keyControls = this.controls.filter((control: IControl) => !control.ignoreForEUPL);
        let ready = keyControls.length > 0;
        for (const control of keyControls) {
            if (!control.data || !control.data.renderTime) {
                ready = false;
                break;
            }
        }
        return ready;
    }
    private setEUPL() {
        if (!this.isCollected('EUPL')) {
            let eupl: number = 0;
            for (const control of this.controls) {
                if (!control.ignoreForEUPL && control.data && control.data.renderTime && eupl < control.data.renderTime) {
                    eupl = control.data.renderTime;
                }
            }

            this._rumoneData.EUPL = eupl;
            if (typeof this._EUPLComputedHandler === 'function') {
                this._EUPLComputedHandler(eupl);
            }
        }
    }
    private setBrowseInfo() {
        this._rumoneData.Browser = this._platformDetection.browserName + this._platformDetection.browserMajor;
        this._rumoneData.BrowserIsMobile = this._platformDetection.isMobile;
    }
    private _collectMemoryData() {
        if (!this.isCollected('UsedJSHeapSize') &&
            performance && performance.memory) {
            this._rumoneData.UsedJSHeapSize = performance.memory.usedJSHeapSize;
            this._rumoneData.TotalJSHeapSize = performance.memory.totalJSHeapSize;
        }
    }
    private setAPIDataToRUMOne() {
        var calls: number = 0;
        var durationSum: number = 0;
        for (var index = 0; index < this.apis.length; index++) {
            var apiData: APICallPerformanceData = this.apis[index];
            if (apiData) {
                calls++;
                durationSum += apiData.duration;
                // only take the function part of the API url to avoid cosmos data scrubber
                // url before processing: /teams/SPGroups/_api/web/GetList(@listUrl)/RenderListDataAsStream?Paged=TRUE&p_FileLeafRef=test%2eurl&p_ID=213&PageFirstRow=121&View=6eab4254-2f2f-4086-91c0-549ae900cc93&@listUrl=%27%2Fteams%2FSPGroups%2FVNextDocLib%27"
                // url after processing: RenderListDataAsStream
                apiData.url = apiData.url.split("/").map((s: string) => { return s.split("?")[0]; }).slice(-1)[0].replace(/\(.*?\)/g, '()');
            }
        }
        this._rumoneData.APICallCount = calls;
        this._rumoneData.APICallDurationSum = durationSum;
        this._rumoneData.APICalls = JSON.stringify(this.apis);
    }
    private setReferrer() {
        this._rumoneData.Referrer = window.document && document.referrer;
    }
    private getW3cTimingName(timingName: string): string {
        if (timingName !== 'secureConnectionStart') {
            return 'W3c' + timingName.charAt(0).toUpperCase() + timingName.slice(1);
        } else {
            return 'W3cSecureConnectStart';  // to workaround a RUMOne schema issue W3cSecureConnectStart should be W3cSecureConnectionStart
        }
    }

    private uploadPerfData(emergency?: boolean) {
        if (this._excludePageData) {
            this.dataState = PerformanceDataState.Skipped;
            this.logMessageInConsole('Uploading perf data skipped as requested by the consumer');
        } else if (!emergency && this.dataState !== PerformanceDataState.ReadyToUpload &&
            this.dataState !== PerformanceDataState.TimeOut) {
            console.error(`Error: Uploading perf data called with wrong data state ${this.dataState}`);
        } else if (!this.loggingFunc) {
            console.error('Uploading perf data skipped as loggingFunc is not defined');
        } else if (!this._rumoneData) {
            console.error('Error: Uploading perf data called but perf data is not available');
        } else {
            this.loggingFunc("RUMOne", this.getPerformanceData());
            this.dataState = PerformanceDataState.Uploaded;
        }
    }
}